import React, { lazy } from 'react';
import { Navigate } from 'react-router-dom';

// Attendance consolidated into the shared /attendance workspace (routes/index.tsx);
// legacy paths redirect to its tabs.
const redirectTo = (to: string) => () => React.createElement(Navigate, { to, replace: true });

const SalaryRevisionList = lazy(() => import('../../pages/SalaryRevision/SalaryRevisionList'));
const SalaryRevisionDetail = lazy(() => import('../../pages/SalaryRevision/SalaryRevisionDetail'));
const MeritMatrix = lazy(() => import('../../pages/SalaryRevision/MeritMatrix'));
const PoliciesPage = lazy(() => import('../../pages/Policies/Policies'));


const HRDashboard = lazy(() => import('../../pages/Dashboard/HR'));
const HrDocuments = lazy(() => import('../../pages/HrDocuments/HrDocuments'));
const HRRequest = lazy(() => import('../../pages/HrRequest/HrRequest'));
const Feed = lazy(() => import('../../pages/Feed'));
const Employees = lazy(() => import('../../pages/Employees/AllEmployeesDetail'));
const LeaveRequests = lazy(() => import('../../pages/HrPayroll/Leaves'));
const OvertimeRequests = lazy(() => import('../../pages/HrPayroll/OvertimeSubmission'));
const WFHRequests = lazy(() => import('../../pages/HrPayroll/WFHRequests'));
const HRExitManagement = lazy(() => import('../../pages/Resignation/HRExitManagement'));
const ExitWorkspace = lazy(() => import('../../pages/ExitWorkFlow/ExitWorkspace'));
const AttendanceSheet = lazy(() => import('../../pages/Employees/AttendanceSheet'));
const AttendanceReport = lazy(() => import('../../pages/Employees/AttendanceReport'));
const AttendanceImport = lazy(() => import('../../pages/Employees/AttendanceImport'));
const SalaryStructure = lazy(() => import('../../pages/HrPayroll/SalaryStruture'));
const BonusManagement = lazy(() => import('../../pages/HrPayroll/PayrollBonus'));
const DeductionManagement = lazy(() => import('../../pages/HrPayroll/PayrollDeduction'));
const Payslips = lazy(() => import('../../pages/HrPayroll/Payslips'));
const LeaveTypes = lazy(() => import('../../pages/Leaves & Holidays/leaveTypes'));
const AttendanceAnalyticsPage = lazy(() => import('../../pages/Analytics/AttendanceAnalytics'));
const TraitsManagement = lazy(() => import('../../pages/Traits/TraitsManagement'));
const EmployeeTraitsManagement = lazy(() => import('../../pages/EmployeeTraits/EmployeeTraitsManagement'));
const IssueTicketsPage = lazy(() => import('../../pages/IssueTickets/IssueTicketsPage'));
const Warnings = lazy(() => import('../../pages/Warnings/Warnings'));
const WarningRules = lazy(() => import('../../pages/WarningRules/WarningRules'));
const WarningTypes = lazy(() => import('../../pages/WarningTypes/WarningTypes'));
const AppraisalCycles = lazy(() => import('../../pages/AppraisalsManagement/AppraisalCycle/AppraisalCycle'));
const AppraisalTemplates = lazy(() => import('../../pages/AppraisalsManagement/AppraisalTemplate/AppraisalTemplate'));
const Appraisals = lazy(() => import('../../pages/AppraisalsManagement/Appraisals/Appraisals'));
const AppraisalDetail = lazy(() => import('../../pages/AppraisalsManagement/Appraisals/AppraisalDetail'));
const ManagerPendingAppraisals = lazy(() => import('../../pages/AppraisalsManagement/Appraisals/ManagerPendingAppraisals'));
const HRPendingAppraisals = lazy(() => import('../../pages/AppraisalsManagement/Appraisals/HRPendingAppraisals'));
const CycleTemplateMapping = lazy(() => import('../../pages/AppraisalsManagement/AppraisalCycle/CycleTemplateMapping'));
const BulkAssign = lazy(() => import('../../pages/AppraisalsManagement/Appraisals/BulkAssign'));
const PendingApprovals = lazy(() => import('../../pages/Approvals/PendingApprovals'));
const HrAttendanceRequests = lazy(() => import('../../pages/AttendanceRegularization/HrAttendanceRequests'));
const EmployeeDocuments = lazy(() => import('../../pages/EmployeeDocuments/EmployeeDocuments'));
const ExpiringDocuments = lazy(() => import('../../pages/EmployeeDocuments/ExpiringDocuments'));
const MissingDocuments = lazy(() => import('../../pages/EmployeeDocuments/MissingDocuments'));
const LoanTypesPage = lazy(() => import('../../pages/Loans/LoanTypesPage'));
const HrLoansPage = lazy(() => import('../../pages/Loans/HrLoansPage'));
const Roster = lazy(() => import('../../pages/Roster/Roster'));
const RotationPatterns = lazy(() => import('../../pages/Roster/RotationPatterns'));

const hrRoutesConfig = [
    { path: '/v1/hr/dashboard', component: HRDashboard },
    { path: '/v1/hr/documents', component: HrDocuments },
    { path: '/v1/hr/requests', component: HRRequest },
    { path: '/v1/hr/feed', component: Feed },
    { path: '/v1/hr/employees', component: Employees },
    // Legacy attendance paths → shared /attendance workspace (defined in routes/index.tsx)
    { path: '/v1/hr/leave-requests', component: redirectTo('/attendance?tab=approvals') },
    { path: '/v1/hr/overtime-requests', component: redirectTo('/attendance?tab=approvals') },
    { path: '/v1/hr/wfh-requests', component: redirectTo('/attendance?tab=approvals') },
    { path: '/v1/hr/team-resignations', component: HRExitManagement },
    { path: '/v1/hr/exit-workspace/:resignationId', component: ExitWorkspace },
    { path: '/v1/hr/attendance', component: redirectTo('/attendance?tab=team') },
    // Report/import remain standalone (reachable from the attendance sheet actions)
    { path: '/v1/hr/attendance-report', component: AttendanceReport },
    { path: '/v1/hr/attendance-import', component: AttendanceImport },
    { path: '/v1/hr/salary-structure', component: SalaryStructure },
    { path: '/v1/payslips-management', component: Payslips },
    { path: '/v1/hr/bonus-management', component: BonusManagement },
    { path: '/v1/hr/deduction-management', component: DeductionManagement },
    { path: '/v1/hr/leave-types', component: redirectTo('/attendance?tab=settings') },
    { path: '/v1/hr/attendance-analytics', component: redirectTo('/attendance?tab=insights') },
    { path: '/v1/hr/traits-management', component: TraitsManagement },
    { path: '/v1/hr/employee-traits-management', component: EmployeeTraitsManagement },
    { path: '/v1/hr/issue-tickets', component: IssueTicketsPage },
    { path: '/v1/hr/warnings', component: Warnings },
    { path: '/v1/hr/warning-rules', component: WarningRules },
    { path: '/v1/hr/warning-types', component: WarningTypes },
    { path: '/v1/hr/appraisals-cycle', component: AppraisalCycles },
    { path: '/v1/hr/appraisals-templates', component: AppraisalTemplates },
    { path: '/v1/hr/appraisals', component: Appraisals },
    { path: '/v1/hr/appraisals/pending/manager', component: ManagerPendingAppraisals },
    { path: '/v1/hr/appraisals/pending/hr', component: HRPendingAppraisals },
    { path: '/v1/hr/appraisals/:id', component: AppraisalDetail },
    { path: '/v1/hr/appraisals-cycle/:cycleId/template-mapping', component: CycleTemplateMapping },
    { path: '/v1/hr/appraisals-cycle/:cycleId/bulk-assign', component: BulkAssign },

    // Approval Engine
    { path: '/v1/pending-approvals', component: PendingApprovals },

    // Attendance Regularization → workspace Approvals tab
    { path: '/v1/hr/attendance-regularization', component: redirectTo('/attendance?tab=approvals') },

    // Employee Document Management
    { path: '/v1/hr/employee-documents', component: EmployeeDocuments },
    { path: '/v1/hr/employee-documents/expiring', component: ExpiringDocuments },
    { path: '/v1/hr/employee-documents/missing', component: MissingDocuments },

    // Loan Management
    { path: '/v1/hr/loan-types', component: LoanTypesPage },
    { path: '/v1/hr/loans', component: HrLoansPage },

    // Shift Rotation & Roster → workspace Settings tab
    { path: '/v1/hr/roster', component: redirectTo('/attendance?tab=settings') },
    { path: '/v1/hr/rotation-patterns', component: redirectTo('/attendance?tab=settings') },

    // Salary Revision / Merit
    // NOTE: :id MUST come after the list route so React Router tries list first
    { path: '/v1/hr/policies', component: PoliciesPage },
    { path: '/v1/hr/salary-revisions', component: SalaryRevisionList },
    { path: '/v1/hr/merit-matrix', component: MeritMatrix },
    { path: '/v1/hr/salary-revisions/:id', component: SalaryRevisionDetail },

    // NOTE: Onboarding, Offboarding & Offer Templates moved to recruiter
    // routes (see recruiterRoutesConfig.ts → /v1/recruiter/*).

];

export default hrRoutesConfig;
